import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Check, Gift, Ticket } from "@phosphor-icons/react";
import { PlanComparison, pricingJsonLd } from "@/components/PlanComparison";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";

const interp = (s, free) => (typeof s === "string" ? s.replaceAll("{FREE}", String(free)) : s);
const PLAN_NAMES = { "1mo": "Monthly", annual: "Annual", family_annual: "Family" };
const promoLabel = (d) => (d.all_plans !== false ? `${d.percent_off}% off`
  : Object.entries(d.plan_percents || {}).filter(([, v]) => v > 0).map(([k, v]) => `${PLAN_NAMES[k] || k} ${v}% off`).join(" · "));

export default function Pricing() {
  const { user, refresh } = useAuth();
  const { flags, limits, loading: flagsLoading } = useSiteFlags();
  const FREE = limits?.free_recipe_limit ?? 10;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  // Promo / discount code applied at checkout. Validated server-side before
  // creating the Stripe session so we never display a "fake" discount.
  const [promo, setPromo] = useState("");
  const [promoValid, setPromoValid] = useState(null); // {code, percent_off} | null
  const [promoChecking, setPromoChecking] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/site-content/pricing").then((r) => setContent(r.data.content)).catch(() => {});
  }, []);

  // Arriving from the promo banner (?code=XYZ): prefill and validate the code.
  useEffect(() => {
    const c = (new URLSearchParams(window.location.search).get("code") || "").trim().toUpperCase();
    if (!c) return;
    setPromo(c);
    api.post("/discount-codes/validate", { code: c })
      .then(({ data }) => { setPromoValid(data); toast.success(`${promoLabel(data)} applied`); })
      .catch(() => {});
  }, []);

  const checkPromo = async () => {
    const c = promo.trim().toUpperCase();
    if (!c) { setPromoValid(null); return; }
    setPromoChecking(true);
    try {
      const { data } = await api.post("/discount-codes/validate", { code: c });
      setPromoValid(data);
      toast.success(`${promoLabel(data)} applied`);
    } catch (e) {
      setPromoValid(null);
      toast.error(e?.response?.data?.detail || "Invalid promo code");
    } finally { setPromoChecking(false); }
  };

  const subscribe = async (tier) => {
    if (!user) { nav(`/register?next=/pricing`); return; }
    setLoading(true);
    try {
      // Real Stripe Checkout — backend defines the price; we only pass the
      // package id + origin so the success/cancel URLs are constructed safely.
      const body = {
        package_id: tier,
        origin: window.location.origin,
      };
      if (promoValid?.code) body.discount_code = promoValid.code;
      const { data } = await api.post("/checkout/session", body);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout — try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const redeem = async () => {
    if (!user) { nav(`/register?next=/pricing`); return; }
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/billing/redeem-gift", { code });
      toast.success(data?.tier === "family_owner"
        ? "Gift code redeemed! You now own a Family Plan — invite up to 4 others from your Profile."
        : "Gift code redeemed! You're now on the 12-month plan.");
      await refresh();
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  // Wait for BOTH page copy and site flags — otherwise the flag-off
  // "coming soon" panel can flash and get baked into crawler snapshots.
  if (!content || flagsLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="pricing-loading">
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  const plans = (content.plans || []).filter((p) => !["3mo", "6mo"].includes(p.id)).map((p) => ({
    ...p,
    featured: p.featured === true || p.featured === "true",
    perksList: typeof p.perks === "string"
      ? p.perks.split("\n").map((s) => s.trim()).filter(Boolean)
      : Array.isArray(p.perks) ? p.perks : [],
  }));

  // Family Plan card — appended if the CMS doesn't already provide one.
  // One £100/year purchase covers the owner plus 4 invited members.
  // Owner manages invites from Profile → Family Plan.
  if (!plans.some((p) => p.id === "family_annual")) {
    plans.push({
      id: "family_annual",
      name: "Family Plan",
      price: "£100",
      period: "year · renews yearly",
      sub: "£20 per person per year · about £8.33 a month · up to 5 people",
      cta: "Choose Family Plan",
      featured: false,
      perksList: [
        "Up to 5 people — family or friends, they needn't live together",
        "Everything in the single plan for each member",
        "You invite up to 4 others by email or link and can remove members",
        "Each member keeps their own private recipes and cookbooks",
        "Renews automatically after 12 months — cancel any time",
      ],
    });
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="pricing"
        fallbackTitle="Pricing: from £2.08/month · Family Plan £100/yr for 5"
        fallbackDescription="Family cookbook subscription from £5 a month or £25 a year (£2.08/month). Family Plan £100/year for 5 people. Cancel any time."
        jsonLd={pricingJsonLd("https://recipefiler.com")}
      />
      <>
      <p className="overline mb-4">{content.overline}</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">
        {content.h1_line1}<br />{content.h1_line2}
      </h1>
      <p className="text-rf-ink-soft text-lg mb-6 max-w-2xl">{interp(content.intro, FREE)}</p>

      {/* Promo / discount code — applied to all paid tiers at Stripe checkout */}
      <div className="mb-10 flex flex-wrap items-center gap-3 max-w-2xl" data-testid="promo-code-panel">
        <Ticket size={22} weight="duotone" className="text-rf-orange" />
        <label className="text-xs font-black uppercase tracking-wider text-rf-ink-soft">Promo code</label>
        <input
          data-testid="promo-code-input"
          value={promo}
          onChange={(e) => { setPromo(e.target.value.toUpperCase()); setPromoValid(null); }}
          onKeyDown={(e) => e.key === "Enter" && checkPromo()}
          placeholder="e.g. LAUNCH20"
          className="input-rf font-mono uppercase tracking-wider flex-1 min-w-[200px] max-w-[260px]"
        />
        <button
          data-testid="promo-code-apply"
          onClick={checkPromo}
          disabled={promoChecking || !promo.trim()}
          className="btn-outline-ink !py-2.5 !px-4 text-sm"
        >
          {promoChecking ? "Checking…" : "Apply"}
        </button>
        {promoValid && (
          <span data-testid="promo-applied" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800">
            ✓ {promoLabel(promoValid)} — applied at checkout
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {plans.map((p) => (
          <div
            key={p.id}
            data-testid={`pricing-card-${p.id}`}
            className={`relative rounded-3xl p-7 border transition-all hover-lift ${p.featured ? "bg-rf-ink text-rf-cream border-rf-ink" : "bg-white border-rf-ink/10"}`}
          >
            {p.featured && (
              <div className="absolute -top-3 left-6 bg-rf-amber text-rf-ink text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Most popular
              </div>
            )}
            <p className={`overline mb-3 ${p.featured ? "text-rf-amber" : ""}`}>{p.name}</p>
            <div className="mb-6">
              {(() => {
                const pct = promoValid && p.id !== "free" ? (promoValid.plan_percents?.[p.id] ?? promoValid.percent_off) : 0;
                const m = String(p.price || "").match(/^(\D*)([\d.,]+)(.*)$/);
                const base = m ? parseFloat(m[2].replace(/,/g, "")) : NaN;
                if (!pct || !m || !base) {
                  return <span className={`font-display text-5xl font-black ${p.featured ? "text-rf-cream" : "text-rf-ink"}`}>{p.price}</span>;
                }
                const now = Math.max(0.5, Math.round(base * (100 - pct)) / 100);
                return (
                  <span className="inline-flex items-baseline gap-3 flex-wrap" data-testid={`pricing-discounted-${p.id}`}>
                    <span className={`font-display text-5xl font-black ${p.featured ? "text-rf-cream" : "text-rf-ink"}`}>{m[1]}{now.toFixed(now % 1 ? 2 : 0)}{m[3]}</span>
                    <s className={`text-xl font-bold ${p.featured ? "text-rf-cream/50" : "text-rf-ink-soft"}`} data-testid={`pricing-original-${p.id}`}>{p.price}</s>
                  </span>
                );
              })()}
              {p.id !== "free" && p.period && (
                <span className={`block mt-1 text-sm ${p.featured ? "text-rf-cream/60" : "text-rf-ink-soft"}`}>/ {p.period}</span>
              )}
              {p.sub && !(promoValid && /£\s?\d/.test(p.sub) && (promoValid.plan_percents?.[p.id] ?? promoValid.percent_off) > 0) && (
                <p className={`mt-2 text-xs font-semibold ${p.featured ? "text-rf-amber" : "text-rf-orange"}`}>{p.sub}</p>
              )}
              {promoValid && p.id !== "free" && (
                (promoValid.plan_percents?.[p.id] ?? promoValid.percent_off) > 0 ? (
                  <span data-testid={`pricing-promo-${p.id}`} className="inline-block mt-2 text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">
                    {promoValid.plan_percents?.[p.id] ?? promoValid.percent_off}% off with {promoValid.code}
                  </span>
                ) : (
                  <span data-testid={`pricing-promo-${p.id}`} className={`block mt-2 text-[11px] font-semibold ${p.featured ? "text-rf-cream/60" : "text-rf-ink-soft"}`}>
                    {promoValid.code} doesn&apos;t apply to this plan
                  </span>
                )
              )}
            </div>
            <ul className="space-y-2 mb-7 text-sm">
              {(p.perksList || []).map((perk) => (
                <li key={perk} className={`flex items-start gap-2 ${p.featured ? "text-rf-cream/90" : "text-rf-ink-soft"}`}>
                  <Check size={16} weight="bold" className={p.featured ? "text-rf-amber mt-1 shrink-0" : "text-rf-orange mt-1 shrink-0"} />
                  <span>{interp(perk, FREE)}</span>
                </li>
              ))}
            </ul>
            {p.id === "free" ? (
              <Link to="/register" data-testid={`pricing-${p.id}-cta`} className="btn-outline-ink w-full">{p.cta}</Link>
            ) : (
              <button
                disabled={loading}
                data-testid={`pricing-${p.id}-cta`}
                onClick={() => subscribe(p.id)}
                className={p.featured ? "btn-amber w-full" : "btn-ink w-full"}
              >
                {p.cta}
              </button>
            )}
            {p.id !== "free" && (
              <p className="mt-3 text-xs text-center opacity-70" data-testid={`pricing-${p.id}-renewal`}>
                {p.id === "1mo" ? "Renews every month until you cancel." : "Renews after 12 months until you cancel."} Cancel any time in Profile.
              </p>
            )}
          </div>
        ))}
      </div>

      <PlanComparison free={FREE} />

      <div className="grid md:grid-cols-2 gap-6 mb-16">
        {/* Buy a gift — £25 12-month or £100 Family Plan via Stripe; recipient gets a code by email */}
        <BuyGiftPanel user={user} content={content} />

        {/* Redeem an existing code */}
        <div className="bg-rf-cream-alt rounded-3xl p-10" data-testid="pricing-gift-panel">
          <Gift size={36} weight="duotone" className="text-rf-orange mb-3" />
          <h2 className="font-display font-black text-3xl text-rf-ink mb-3">Redeem a gift code</h2>
          <p className="text-rf-ink-soft mb-4 leading-relaxed">{content.gift_body}</p>
          <div className="flex gap-3">
            <input
              data-testid="gift-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={content.gift_placeholder}
              className="input-rf font-mono uppercase tracking-wider"
            />
            <button data-testid="gift-code-redeem" onClick={redeem} disabled={loading} className="btn-amber !rounded-xl !px-6">{content.gift_cta}</button>
          </div>
        </div>
      </div>
      </>
    </div>
  );
}



const GIFTS = {
  annual: { price: "£25", title: "Gift 12 months · £25", desc: "the recipient gets a 12-month Recipe Filer gift code by email within seconds." },
  family_annual: { price: "£100", title: "Gift the Family Plan · £100", desc: "the recipient gets a Family Plan gift code by email — they become the plan owner and can invite up to 4 others." },
};

// BuyGiftPanel — £25 / £100 Stripe gift purchase. Collects recipient details, posts to
// /api/checkout/gift-session and redirects to Stripe. After payment the
// recipient receives the code by email; the buyer sees a confirmation on
// /payment-success.
function BuyGiftPanel({ user, content }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [giftType, setGiftType] = useState("annual");
  const gift = GIFTS[giftType];
  const openGift = (t) => { setGiftType(t); setOpen(true); };
  const [form, setForm] = useState({
    recipient_email: "", recipient_name: "", personal_message: "",
    buyer_email: "", buyer_name: "",   // only used for anonymous buyers
  });

  const submit = async (e) => {
    e.preventDefault();
    // 2026-08-27: anonymous buyers can now purchase gifts. Signed-in buyers
    // don't need to fill the buyer_* fields (Stripe uses their account email).
    setBusy(true);
    try {
      const body = {
        recipient_email: form.recipient_email,
        recipient_name: form.recipient_name,
        personal_message: form.personal_message,
        origin: window.location.origin,
        gift_type: giftType,
      };
      if (!user) {
        body.buyer_email = form.buyer_email;
        body.buyer_name = form.buyer_name;
      }
      const { data } = await api.post("/checkout/gift-session", body);
      if (data?.url) { window.location.href = data.url; return; }
      throw new Error("No checkout URL returned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start gift checkout");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-rf-ink text-rf-cream rounded-3xl p-10" data-testid="pricing-buy-gift-panel">
      <Gift size={36} weight="duotone" className="text-rf-amber mb-3" />
      <h2 className="font-display font-black text-3xl mb-3">Buy a gift</h2>
      <p className="text-rf-cream/80 mb-3 leading-relaxed">
        Give the gift of saved family recipes. Choose 12 months of Recipe Filer for one cook for £25,
        or the £100 Family Plan, which covers them plus up to 4 family members or friends for a whole year.
      </p>
      <p className="text-rf-cream/60 text-sm mb-5 leading-relaxed">
        They&apos;ll get a gift code by email within seconds of payment. Codes never expire until redeemed.
      </p>
      <div className="flex flex-wrap gap-3">
        <button data-testid="buy-gift-open" onClick={() => openGift("annual")} className="btn-amber !rounded-xl !px-6">
          Buy a £25 gift →
        </button>
        <button data-testid="buy-gift-family-open" onClick={() => openGift("family_annual")} className="btn-amber !rounded-xl !px-6">
          Buy a £100 Family gift →
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-rf-cream border-rf-ink/10 max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="gift-dialog-title" className="font-display text-2xl font-black text-rf-ink">{gift.title}</DialogTitle>
            <DialogDescription className="text-sm text-rf-ink-soft">
              Pay {gift.price} with Stripe — {gift.desc}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {!user && (
              <div className="rounded-xl border border-rf-amber/40 bg-rf-amber/10 p-3 space-y-3" data-testid="gift-anon-buyer-block">
                <p className="text-[11px] font-black uppercase tracking-widest text-rf-ink-soft">
                  About you (so we can email your Stripe receipt)
                </p>
                <div>
                  <label className="overline">Your name</label>
                  <input
                    data-testid="gift-buyer-name"
                    required
                    value={form.buyer_name}
                    onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                    placeholder="e.g. Alex"
                    className="input-rf mt-1"
                  />
                  <p className="text-[11px] text-rf-ink-soft mt-1">Shown on the recipient&apos;s gift email as the sender.</p>
                </div>
                <div>
                  <label className="overline">Your email</label>
                  <input
                    data-testid="gift-buyer-email"
                    required
                    type="email"
                    value={form.buyer_email}
                    onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                    placeholder="you@example.com"
                    className="input-rf mt-1"
                  />
                  <p className="text-[11px] text-rf-ink-soft mt-1">
                    Stripe sends your receipt here. No Recipe Filer account is created — sign up any time later.
                  </p>
                </div>
              </div>
            )}
            <div>
              <label className="overline">Recipient name</label>
              <input
                data-testid="gift-recipient-name"
                required
                value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                placeholder="e.g. Mum"
                className="input-rf mt-1"
              />
            </div>
            <div>
              <label className="overline">Recipient email</label>
              <input
                data-testid="gift-recipient-email"
                required
                type="email"
                value={form.recipient_email}
                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                placeholder="recipient@example.com"
                className="input-rf mt-1"
              />
              <p className="text-[11px] text-rf-ink-soft mt-1">We&apos;ll email the code immediately after Stripe confirms your payment.</p>
            </div>
            <div>
              <label className="overline">Personal message (optional)</label>
              <textarea
                data-testid="gift-personal-message"
                rows={3}
                maxLength={500}
                value={form.personal_message}
                onChange={(e) => setForm({ ...form, personal_message: e.target.value })}
                placeholder="Happy birthday — for all those recipes scribbled on the back of envelopes."
                className="input-rf mt-1"
              />
            </div>
            <button data-testid="gift-checkout-submit" type="submit" disabled={busy} className="btn-amber w-full !rounded-xl">
              {busy ? "Opening Stripe…" : `Pay ${gift.price} with Stripe →`}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
